import type { PostHog } from 'posthog-js'
import { Env } from 'infra/env'
import { Id } from 'types/definitions'
import type Member from 'iam/models/Member'
import type { SessionRecordingService } from './SessionRecordingService'

/**
 * PostHog session recording implementation.
 * Handles user identification and session lifecycle for analytics.
 */
export class PostHogSessionRecordingService implements SessionRecordingService {
  private _postHog: PostHog | null = null

  // Associates the current user with their PostHog session for tracking
  async identify(member: Member) {
    const postHog = await this._getPostHog()
    postHog.identify(Id.assert(member.user.id), {
      first_name: member.givenName,
      last_name: member.familyName,
      name: member.displayName,
      email: member.email,
      workspace_id: Id.assert(member.workspace.id),
      id: Id.assert(member.user.id),
    })
  }

  // Stops session recording and clears user data on logout
  async reset() {
    const postHog = await this._getPostHog()
    postHog.stopSessionRecording()
    postHog.reset()
  }

  // Lazily initializes PostHog on first use via dynamic import
  private async _getPostHog(): Promise<PostHog> {
    if (!this._postHog) {
      const { posthog } = await import('posthog-js')
      this._postHog = posthog.init(Env.POSTHOG_KEY, {
        api_host: 'https://us.i.posthog.com',
        session_recording: {
          maskTextSelector: '[data-private]',
          maskInputOptions: { password: true, email: true },
          // Redact sensitive network requests from recordings
          maskCapturedNetworkRequestFn: (request: any) => {
            if (request.name?.includes('/messages')) {
              return {
                ...request,
                requestBody: '[Redacted]',
                responseBody: '[Redacted]',
              }
            }
            if (request.name?.includes('__authorization')) {
              return null
            }
            return request
          },
        },
      })
    }
    return this._postHog
  }

  // Factory method for creating service instances
  static create() {
    return new PostHogSessionRecordingService()
  }
}
